import { forwardRef, Ref, useState, ReactElement, ChangeEvent, useRef, useEffect } from 'react';
import {
  Avatar,
  Link,
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  lighten,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  TextField,
  Theme,
  Tooltip,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  Slide
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { TransitionProps } from '@mui/material/transitions';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone';
import FindInPageTwoToneIcon from '@mui/icons-material/FindInPageTwoTone';
import ChevronRightTwoToneIcon from '@mui/icons-material/ChevronRightTwoTone';
import CloseIcon from '@mui/icons-material/Close';
import CloseButton from 'src/components/CloseButton';

const Transition = forwardRef(function Transition(
  props: TransitionProps & { children: ReactElement<any, any> },
  ref: Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const DialogWrapper = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-container': {
    alignItems: 'flex-start',
    [theme.breakpoints.down('md')]: {
      alignItems: 'stretch'
    }
  },
  '& .MuiDialog-paperScrollPaper': {
    maxHeight: 480,
    minHeight: 220,
    minWidth: 520,
    [theme.breakpoints.down('md')]: {
      height: '100vh',
      width: '100vw',
      maxHeight: '100vh',
      margin: 0,
      borderRadius: 0,
      minWidth: 'auto',
      minHeight: 'auto'
    }
  }
}));

const SearchInputWrapper = styled(TextField)(({ theme }) => `
  background: ${theme.palette.mode === 'dark' ? theme.colors.alpha.black[70] : theme.colors.alpha.white[100]};
  border-radius: 6px;
  position: relative;
  z-index: 10;

  .MuiInputBase-input {
    font-size: ${theme.typography.pxToRem(17)};
    color: ${theme.palette.mode === 'dark' ? theme.colors.alpha.white[70] : theme.colors.alpha.black[100]};
  }

  .MuiInputLabel-root {
    color: ${theme.palette.mode === 'dark' ? theme.colors.alpha.white[70] : theme.colors.alpha.black[70]};
  }

  .MuiInputAdornment-root {
    color: ${theme.palette.mode === 'dark' ? theme.colors.alpha.white[70] : theme.colors.alpha.black[70]};
  }
`);

const DialogTitleWrapper = styled(DialogTitle)(
  ({ theme }) => `
    background: ${theme.colors.alpha.black[5]};
    padding: ${theme.spacing(3)}
`
);

function HeaderSearch() {
  const [openSearchResults, setOpenSearchResults] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [recent, setRecent] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('recentSearches') || '[]'); } catch { return []; }
  });

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setSearchValue(event.target.value);

    if (event.target.value) {
      if (!openSearchResults) {
        setOpenSearchResults(true);
      }
    } else {
      setOpenSearchResults(false);
    }
  };

  const handleClickOpen = () => { setOpen(true); };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const commitSearch = () => {
    if (!searchValue.trim()) return;
    const next = [searchValue.trim(), ...recent.filter(r => r !== searchValue.trim())].slice(0, 5);
    setRecent(next);
    try { localStorage.setItem('recentSearches', JSON.stringify(next)); } catch {}
  };

  return (
    <>
      <Tooltip arrow title="Search">
        <IconButton color="primary" onClick={handleClickOpen} sx={{ width: 44, height: 44 }} aria-label="open search">
          <SearchTwoToneIcon />
        </IconButton>
      </Tooltip>

      <DialogWrapper
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        fullScreen={isMobile}
        fullWidth={!isMobile}
        maxWidth={isMobile ? undefined : 'md'}
        disableScrollLock
        BackdropProps={{ className: 'search-modal-backdrop' }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 2 }}>
          Search
          <CloseButton aria-label="close" onClick={handleClose}>
            <CloseIcon />
          </CloseButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <SearchInputWrapper
            sx={{ mt: 2 }}
            value={searchValue}
            inputRef={inputRef}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchTwoToneIcon />
                </InputAdornment>
              )
            }}
            placeholder="Search terms here..."
            fullWidth
            label="Search"
            variant="outlined"
          />
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => { commitSearch(); /* Perform search route if needed */ }}
            >
              Search
            </Button>
          </Box>
          <Divider sx={{ my: 1 }} />
          {/* Recent searches */}
          {recent.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="overline">Recent</Typography>
              <List disablePadding>
                {recent.map((q) => (
                  <ListItem key={q} disablePadding>
                    <ListItemButton onClick={() => { setSearchValue(q); setOpenSearchResults(true); }}>
                      <ListItemText primary={q} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
              <Divider sx={{ my: 1 }} />
            </Box>
          )}
          {openSearchResults && (
            <>
              <Box
                sx={{ pt: 0, pb: 1 }}
                display="flex"
                justifyContent="space-between"
              >
                <Typography variant="body2" component="span">
                  Search results for{' '}
                  <Typography
                    sx={{ fontWeight: 'bold' }}
                    variant="body1"
                    component="span"
                  >
                    {searchValue}
                  </Typography>
                </Typography>
                <Link href="#" variant="body2" underline="hover">
                  Advanced search
                </Link>
              </Box>
              <Divider sx={{ my: 1 }} />
              <List disablePadding>
                <ListItem disablePadding>
                  <ListItemButton>
                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            background: (theme: Theme) =>
                              theme.palette.secondary.main
                          }}
                        >
                          <FindInPageTwoToneIcon />
                        </Avatar>
                      </ListItemAvatar>
                    </Box>
                    <Box flex="1">
                    <Box display="flex" justifyContent="space-between">
                      <Link
                        href="#"
                        underline="hover"
                        sx={{ fontWeight: 'bold' }}
                        variant="body2"
                      >
                        Dashboard for Healthcare Platform
                      </Link>
                    </Box>
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{
                        color: (theme: Theme) =>
                          lighten(theme.palette.secondary.main, 0.5)
                      }}
                    >
                      This page contains all the necessary information for
                      managing all hospital staff.
                    </Typography>
                  </Box>
                  <ChevronRightTwoToneIcon />
                  </ListItemButton>
                </ListItem>
                <Divider sx={{ my: 1 }} component="li" />
                <ListItem disablePadding>
                  <ListItemButton>
                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            background: (theme: Theme) =>
                              theme.palette.secondary.main
                          }}
                        >
                          <FindInPageTwoToneIcon />
                        </Avatar>
                      </ListItemAvatar>
                    </Box>
                    <Box flex="1">
                      <Box display="flex" justifyContent="space-between">
                        <Link
                          href="#"
                          underline="hover"
                          sx={{ fontWeight: 'bold' }}
                          variant="body2"
                        >
                          Example Projects Application
                        </Link>
                    </Box>
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{
                        color: (theme: Theme) =>
                          lighten(theme.palette.secondary.main, 0.5)
                      }}
                    >
                      This is yet another search result pointing to a app page.
                    </Typography>
                  </Box>
                  <ChevronRightTwoToneIcon />
                  </ListItemButton>
                </ListItem>
                <Divider sx={{ my: 1 }} component="li" />
                <ListItem disablePadding>
                  <ListItemButton>
                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            background: (theme: Theme) =>
                              theme.palette.secondary.main
                          }}
                        >
                        <FindInPageTwoToneIcon />
                      </Avatar>
                    </ListItemAvatar>
                  </Box>
                  <Box flex="1">
                    <Box display="flex" justifyContent="space-between">
                      <Link
                        href="#"
                        underline="hover"
                        sx={{ fontWeight: 'bold' }}
                        variant="body2"
                      >
                        Search Results Page
                      </Link>
                    </Box>
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{
                        color: (theme: Theme) =>
                          lighten(theme.palette.secondary.main, 0.5)
                      }}
                    >
                      Choose if you would like to show or not this typography
                      section here...
                    </Typography>
                  </Box>
                  <ChevronRightTwoToneIcon />
                  </ListItemButton>
                </ListItem>
              </List>
              <Divider sx={{ mt: 1, mb: 2 }} />
              <Box sx={{ textAlign: 'center' }}>
                <Button color="primary">View all search results</Button>
              </Box>
            </>
          )}
        </DialogContent>
      </DialogWrapper>
    </>
  );
}

export default HeaderSearch;
